import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { UserCard } from '@shared/components/ui/usercard';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import Tooltip from '@shared/components/ui/tooltip/tooltip';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownActions } from '../components/interaction';
import {
  defaultLogisticStatusDot,
  defaultOrderStatusDot,
  getStatusForDropdown,
  logisticStatusColorMap,
  statusColorMap,
} from '../config/orderStatusConfig';
import { StatusBadgeWithDot } from '../components/display/StatusBadgeWithDot';
import { Order } from '../models/Orders';
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

const formatOrderTotal = (order: Order): string => {
  const amount = order.total;
  const currency = order.currency || 'EUR';
  if (amount == null || Number.isNaN(Number(amount))) return '-';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${amount} ${currency}`;
  }
};
const safeRender = (value: any): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value.toString();
  if (typeof value === 'object') {
    if (value.name) return value.name;
    if (value.description) return value.description;
    if (value.title) return value.title;
    if (value.label) return value.label;
    if (value.id) return value.id.toString();
  }
  return '-';
};
const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<Order>[] => {
  return agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
      cell: col.cellRenderer ? () => null : undefined,
    }));
};
interface UseAgGridColumnsProps {
  hasTeamData: boolean;
  selectedColumns?: string[];
  refetch?: () => void;
}
export const useAgGridColumns = ({ hasTeamData, selectedColumns = [], refetch }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('ordersModule');
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('columns.order'),
        sortable: true,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          const errorCount = order.errors?.length || 0;
          const errors = order.errors || [];
          return (
            <div className="text-sm font-medium flex items-center gap-2">
              {errorCount > 0 && (
                <Tooltip
                  content={
                    <div className="bg-error-buttons light:bg-error border-0 rounded-lg p-3 max-w-xs">
                      <div className="space-y-2">
                        {errors.map((error: any, index: number) => (
                          <div key={index} className="flex items-start gap-2">
                            <FontAwesomeIcon
                              icon={faTriangleExclamation}
                              className="text-white text-sm mt-0.5 flex-shrink-0"
                            />
                            <div className="text-white text-sm">
                              {typeof error === 'string'
                                ? error
                                : String(t(`errors.${error.error_key}`, error.error_key || 'Error desconocido'))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                  trigger={
                    <span className="cursor-help">
                      <span className="w-4 h-4 rounded-full bg-error-buttons light:bg-error text-title text-[8px] flex items-center justify-center text-white">
                        {errorCount}
                      </span>
                    </span>
                  }
                  side="right"
                  align="center"
                  sideOffset={-5}
                  className="bg-transparent border-0 shadow-none"
                />
              )}
              {order.ref}
            </div>
          );
        },
      },
      {
        headerName: t('table.products'),
        field: 'products',
        valueGetter: (params) => {
          const order = params.data as Order;
          return order.lines?.reduce((acc: number, line: any) => acc + (line?.qty || 0), 0) || 0;
        },
      },
      {
        headerName: t('table.total'),
        field: 'total',
        sortable: true,
        valueGetter: (params) => {
          const order = params.data as Order;
          return order?.total ?? null;
        },
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm font-medium tabular-nums">{formatOrderTotal(order)}</span>;
        },
      },
      {
        headerName: t('table.shop'),
        field: 'shop',
        sortable: true,
        valueGetter: (params) => params.data.shop?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          if (!order.shop) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return <ShopComponent name={order.shop.name} src={order.shop.image_hash} />;
        },
      },
      {
        headerName: t('table.created_at'),
        field: 'order_created_at',
        sortable: true,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{formatDate(order.order_created_at)}</span>;
        },
      },
      {
        headerName: t('table.ready_to_prepare_at'),
        field: 'ready_to_prepare_at',
        sortable: true,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{formatDate(order.ready_to_prepare_at)}</span>;
        },
      },
      {
        headerName: t('table.status'),
        field: 'status',
        valueGetter: (params) => params.data.status?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          const code = order.status.id;
          const { dot } = statusColorMap[code] ?? defaultOrderStatusDot;
          return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={order.status.description} />;
        },
      },
      {
        headerName: t('table.courier'),
        field: 'courier',
        valueGetter: (params) => params.data.shipment?.courier_name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return order.shipment.courier ? (
            <ShopComponent name={order.shipment.courier?.name} src={order.shipment.courier?.image_hash} />
          ) : (
            <span className="text-sm">-</span>
          );
        },
      },
      {
        headerName: t('table.customer'),
        field: 'customer',
        valueGetter: (params) => params.data.customer?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm font-medium">{safeRender(order.customer?.name)}</span>;
        },
      },
      {
        headerName: t('table.shipment_date'),
        field: 'shipment_processed_at',
        sortable: true,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{formatDate(order.shipment?.processed_at)}</span>;
        },
      },
      {
        headerName: t('table.logistic_status'),
        field: 'logistic_status',
        valueGetter: (params) => params.data.shipment?.status_mapped?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          const statusMapped = order.shipment?.status_mapped;
          if (!statusMapped) {
            return (
              <StatusBadgeWithDot
                className="text-xs"
                dotClassName={defaultLogisticStatusDot.dot}
                label={t('table.no_logistic_status')}
              />
            );
          }
          if (typeof statusMapped === 'string') {
            return (
              <StatusBadgeWithDot
                className="text-xs"
                dotClassName={defaultLogisticStatusDot.dot}
                label={statusMapped}
              />
            );
          }
          const { dot } = logisticStatusColorMap[statusMapped.id] ?? defaultLogisticStatusDot;
          return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={statusMapped.name} />;
        },
      },
      {
        headerName: t('table.tracking_number'),
        field: 'tracking_number',
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm font-mono">{safeRender(order.shipment?.tracking_number)}</span>;
        },
      },
      {
        headerName: t('table.last_logistic_update'),
        field: 'date_tracking_update',
        sortable: true,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{formatDate(order.shipment?.date_tracking_update)}</span>;
        },
      },
      {
        headerName: t('table.payment_method'),
        field: 'payment_method',
        valueGetter: (params) => params.data.payment_method?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{safeRender(order.external_payment_method)}</span>;
        },
      },
      {
        headerName: t('table.phone'),
        field: 'phone',
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{safeRender(order.customer?.phone)}</span>;
        },
      },
      {
        headerName: t('table.postal_code'),
        field: 'postal_code',
        sortable: true,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{safeRender(order.customer?.address?.zip)}</span>;
        },
      },
    ];
    if (hasTeamData) {
      baseColumns.push({
        headerName: t('table.client'),
        field: 'team',
        valueGetter: (params) => params.data.team?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return order.team?.name ? (
            <UserCard name={order.team.name} email="" />
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          );
        },
      });
      baseColumns.push({
        headerName: t('table.preparation'),
        field: 'preparation',
        valueGetter: (params) => params.data.preparation?.code,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          if (!order.preparation) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          // Mapeo de estados de preparación
          const statusMap: Record<string, { label: string; color: string }> = {
            '1': { label: 'Pendiente', color: 'bg-card-border text-muted-foreground' },
            '2': { label: 'En proceso', color: 'bg-bg-warning text-warning' },
            '3': { label: 'Completada', color: 'bg-bg-success text-success' },
            '4': { label: 'Cancelada', color: 'bg-bg-error text-error' },
          };
          const statusInfo = statusMap[order.preparation.status] || {
            label: `Estado ${order.preparation.status}`,
            color: 'bg-card-border text-muted-foreground',
          };
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{order.preparation.code}</span>
              <Badge className={`text-xs ${statusInfo.color} w-fit`}>{statusInfo.label}</Badge>
            </div>
          );
        },
      });
    }
    baseColumns.push({
      headerName: '',
      field: 'actions',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: (params: any) => {
        const order = params.data as Order;
        const status = getStatusForDropdown(order.status.id);
        return (
          <div className="flex justify-end w-full">
            <DropdownActions
              status={status}
              orderId={order.id}
              orderNumber={order.ref}
              onAction={refetch}
              attachments={order.attachments}
            />
          </div>
        );
      },
    });
    return baseColumns;
  }, [t, hasTeamData, refetch]);
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    const alwaysVisibleColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field === 'actions' || field === 'ref' || field === 'unknown';
    });
    const selectableColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    });
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      const result: ColDef[] = [];
      cols.forEach((c) => {
        const id = (c.field || c.headerName || 'unknown') as string;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(c);
        }
      });
      return result;
    };
    if (selectedColumns.length === 0) {
      return dedupeByField([...selectableColumns, ...alwaysVisibleColumns]);
    }
    const selectedSelectableColumns = selectableColumns.filter((col) => {
      const fieldId = col.field || col.headerName || 'unknown';
      return selectedColumns.includes(fieldId);
    });
    const combined = [...selectedSelectableColumns];
    alwaysVisibleColumns.forEach((c) => {
      const field = c.field || c.headerName || 'unknown';
      const exists = combined.some((x) => (x.field || x.headerName || 'unknown') === field);
      if (!exists) combined.push(c);
    });
    return combined;
  }, [allColumnDefs, selectedColumns]);
  const tanStackColumns: ColumnDef<Order>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);
  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
